import { readSettingsNumber } from '@/utils'
import { isNull, isUndefined } from 'lodash'
import { type Ref, computed, ref, watch } from 'vue'

export type TradingMode = 'cex' | 'dex'

export function useCreateSwapOrderSettings(
  settingsForm: Ref | null,
  cacheKeyPostfix: Ref<string>,
  mode: TradingMode,
) {
  const initing = ref(true)

  const leverage = computed(() =>
    readSettingsNumber(
      settingsForm?.value?.states?.leverage?.value,
      leverageCacheKey.value,
      initing.value,
    ),
  )

  const slippage = computed(() =>
    readSettingsNumber(
      settingsForm?.value?.states?.slippage?.value,
      slippageCacheKey.value,
      initing.value,
    ),
  )

  const amount = computed(() =>
    readSettingsNumber(
      settingsForm?.value?.states?.amount?.value,
      amountCacheKey.value,
      initing.value,
    ),
  )

  const jumpRequired = ref(false)
  const leverageCacheKey = computed(() => `leverage/${mode}/${cacheKeyPostfix.value}`)
  const marginModeCacheKey = computed(() => `margin/${mode}/${cacheKeyPostfix.value}`)
  const hedgedModeCacheKey = computed(() => `hedge/${mode}/${cacheKeyPostfix.value}`)
  const slippageCacheKey = computed(() => `slippage/${mode}/${cacheKeyPostfix.value}`)
  const amountCacheKey = computed(() => `amount/${mode}/${cacheKeyPostfix.value}`)
  const jumpCacheKey = computed(() => `jump/${mode}/${cacheKeyPostfix.value}`)

  watch(leverage, (v) => {
    if (!isNull(v) && !isUndefined(v)) {
      localStorage.setItem(leverageCacheKey.value, v.toString())
    }
  })
  watch(slippage, (v) => {
    if (!isNull(v) && !isUndefined(v)) {
      localStorage.setItem(slippageCacheKey.value, v.toString())
    }
  })
  watch(amount, (v) => {
    if (isNull(v)) {
      localStorage.removeItem(amountCacheKey.value)
    } else if (!isNull(v) && !isUndefined(v) && !isNaN(v)) {
      localStorage.setItem(amountCacheKey.value, v.toString())
    }
  })
  watch(jumpRequired, (v) => {
    if (isNull(v)) {
      localStorage.removeItem(jumpCacheKey.value)
    } else if (!isNull(v) && !isUndefined(v)) {
      localStorage.setItem(jumpCacheKey.value, v ? '1' : '0')
    }
  })

  const initSettingsForm = (recommendedAmount: number | null) => {
    initing.value = true
    settingsForm?.value?.setFieldValue('slippage', slippage.value ?? '1')
    settingsForm?.value?.setFieldValue('amount', amount.value ?? recommendedAmount)
    settingsForm?.value?.setFieldValue('leverage', leverage.value ?? '1')
    jumpRequired.value = localStorage.getItem(jumpCacheKey.value) == '1'
    initing.value = false
  }

  return {
    leverage,
    slippage,
    amount,
    jumpRequired,
    leverageCacheKey,
    marginModeCacheKey,
    hedgedModeCacheKey,
    slippageCacheKey,
    amountCacheKey,
    initSettingsForm,
  }
}
